import TabsContainer from 'components/Tabs/Container'
import CampaignsTab from 'containers/userLists/campaigns/CampaignsTab.container'
import CustomersTab from 'containers/userLists/customers/CustomersTab.container'
import DetailHeader from 'containers/userLists/detail/DetailHeader.container'
import CustomerImportImporter from 'containers/userLists/importHistory/CustomerImportImporter.container'
import ImportHistoryTab from 'containers/userLists/importHistory/ImportHistoryTab.container'
import ImportModeModal from 'containers/userLists/importHistory/ImportModeModal'
import useCustomerImportPolling from 'containers/userLists/importHistory/useCustomerImportPolling'
import { buildTranslate } from 'locales'
import React, { useEffect } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import { useDispatch } from 'react-redux'
import { Redirect, RouteComponentProps } from 'react-router-dom'
import { openImportModeModal, setCurrentList } from 'slices/userLists'
import { history } from 'store'

const t = buildTranslate('user_lists.detail')

const DEFAULT_TAB = 'import_history'
const VALID_TABS = ['import_history', 'customers', 'active_campaigns']

type Props = RouteComponentProps<{ id: string; tab?: string }>

const Detail: React.FC<Props> = props => {
  const { id, tab } = props.match.params
  const dispatch = useDispatch()
  const listId = Number(id)
  const isValidId = Number.isInteger(listId) && listId > 0
  const activeTab = tab && VALID_TABS.includes(tab) ? tab : DEFAULT_TAB

  // Detect ?import=true query param to auto-open importer
  useEffect(() => {
    if (!isValidId) return
    const params = new URLSearchParams(props.location.search)
    if (params.get('import') === 'true') {
      dispatch(openImportModeModal(listId))
      // Remove query param to prevent re-opening on navigation, preserving current pathname
      params.delete('import')
      const newSearch = params.toString()
      history.replace({
        pathname: props.location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      })
    }
  }, [dispatch, listId, id, props.location.search, isValidId])

  useEffect(() => {
    if (!isValidId) return
    dispatch(setCurrentList(listId))
    return () => {
      dispatch(setCurrentList(null))
    }
  }, [dispatch, listId, isValidId])

  // Poll for active import status
  useCustomerImportPolling(isValidId ? listId : 0)

  if (!isValidId) {
    return <Redirect to="/user_lists" />
  }

  const handleTabSelect = (selectedTab: string) => {
    history.push(`/user_lists/${id}/${selectedTab}`)
  }

  const tabs = [
    {
      id: 'import_history',
      name: t('tabs.import_history'),
      content: (
        <Tab.Pane key="import_history" eventKey="import_history">
          <ImportHistoryTab listId={listId} />
        </Tab.Pane>
      ),
    },
    {
      id: 'customers',
      name: t('tabs.customers'),
      content: (
        <Tab.Pane key="customers" eventKey="customers">
          <CustomersTab listId={listId} />
        </Tab.Pane>
      ),
    },
    {
      id: 'active_campaigns',
      name: t('tabs.active_campaigns'),
      content: (
        <Tab.Pane key="active_campaigns" eventKey="active_campaigns">
          <CampaignsTab listId={listId} />
        </Tab.Pane>
      ),
    },
  ]

  return (
    <DetailHeader listId={listId}>
      <TabsContainer
        key={activeTab}
        defaultTab={activeTab}
        onSelect={handleTabSelect}
        tabs={tabs}
      />
      <ImportModeModal />
      <CustomerImportImporter />
    </DetailHeader>
  )
}

export default Detail
